import Header from './Header';
import Footer from './Footer';
import AccessibilityWidget from './AccessibilityWidget';
import { Link } from 'react-router-dom';
import { Shield, Zap, Wrench, Leaf, Volume2, Flag, DollarSign, FileCheck, ArrowRight, Check, Star } from 'lucide-react';

export default function WhyGoodmanPage({ onOpenLeadForm, onOpenEstimateForm }) {
  return (
    <div className="min-h-screen bg-white" data-testid="why-goodman-page">
      <Header onOpenLeadForm={onOpenLeadForm} onOpenEstimateForm={onOpenEstimateForm} />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #064e3b 0%, #047857 40%, #059669 100%)' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-20 lg:py-28 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block bg-white/15 backdrop-blur-sm text-emerald-100 text-sm font-semibold px-4 py-1.5 rounded-full mb-6 border border-white/20">Trusted by Homeowners Across Central Florida</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-6">
              Why a Goodman Heat Pump<br />Is the <span className="text-yellow-300">Smart Choice</span>
            </h1>
            <p className="text-lg text-white/85 max-w-2xl mx-auto mb-10">When it comes to keeping your home comfortable year-round without breaking the bank, a Goodman Heat Pump A/C System stands in a class of its own.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/installation" className="inline-flex items-center gap-2 bg-white text-emerald-800 font-bold px-8 py-4 rounded-xl hover:bg-emerald-50 transition-colors text-base shadow-lg">
                View Systems & Pricing <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-gray-900 border-y border-gray-800">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '$150', label: 'Monthly Energy Savings', sub: 'up to' },
              { value: '20+', label: 'Year Lifespan', sub: 'with maintenance' },
              { value: '10 Year', label: 'Factory Warranty', sub: 'Included' },
              { value: 'Top 3', label: 'US News AC Brand', sub: 'ranked nationally' },
            ].map((stat, i) => (
              <div key={i}>
                <div className="text-2xl sm:text-3xl font-black text-yellow-400">{stat.value}</div>
                <div className="text-sm text-white font-medium mt-1">{stat.label}</div>
                <div className="text-xs text-gray-500">{stat.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-16">

        {/* Feature Grid - Top 3 */}
        <div className="grid md:grid-cols-3 gap-6 mb-20">
          {[
            { icon: Wrench, title: 'One System Does It All', color: 'bg-emerald-500', desc: 'Heat and cool your home with one intelligent system. No furnace to service, no separate AC, no seasonal switchovers. Just seamless comfort, 365 days a year.' },
            { icon: Zap, title: 'Exceptional Efficiency', color: 'bg-amber-500', desc: 'SEER2 ratings from 14.3 to 18. Users report energy savings of up to $150 monthly. Upgrading from older systems yields 15-30% reductions in energy bills.' },
            { icon: Shield, title: 'Built to Last', color: 'bg-blue-500', desc: 'Heavy-gauge galvanized steel cabinets with powder-coat finishes. Copper tubing with aluminum fins. With proper maintenance, units last 12-20+ years.' },
          ].map((card, i) => (
            <div key={i} className="group rounded-2xl border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white">
              <div className={`w-14 h-14 ${card.color} rounded-2xl flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform`}>
                <card.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{card.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{card.desc}</p>
            </div>
          ))}
        </div>

        {/* Warranty - Full Width Highlight */}
        <section className="rounded-3xl overflow-hidden mb-20" style={{ background: 'linear-gradient(135deg, #064e3b, #047857)' }}>
          <div className="grid md:grid-cols-2 gap-0">
            <div className="p-10 lg:p-14 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-4">
                <FileCheck className="w-6 h-6 text-yellow-300" />
                <span className="text-emerald-200 font-semibold text-sm uppercase tracking-wider">Industry-Leading Protection</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-black text-white mb-5 leading-tight">Outstanding Warranty Protection</h2>
              <p className="text-emerald-100 leading-relaxed mb-6">Every Goodman system purchased through Appliance Direct comes with a 10 Year Factory Warranty included - one of the strongest warranty programs in the industry.</p>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/10" data-testid="why-goodman-warranty-badge">
                <div className="text-yellow-300 font-black text-2xl">10 Year Factory Warranty</div>
                <div className="text-emerald-200 text-sm font-medium">Included</div>
              </div>
            </div>
            <div className="bg-emerald-800/50 p-10 lg:p-14 flex items-center justify-center">
              <div className="space-y-4 w-full">
                {[
                  '10 Year Factory Warranty included',
                  'Covers the compressor - the heart of your system',
                  'Backed by Goodman, installed by licensed partners',
                  'One of the strongest programs in HVAC industry',
                  'True peace of mind for your investment',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-yellow-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-yellow-300" />
                    </div>
                    <span className="text-white text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Alternating Feature Sections */}
        <div className="space-y-20 mb-20">

          {/* Eco-Friendly */}
          <section className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-green-600" />
                </div>
                <span className="text-green-600 font-semibold text-sm uppercase tracking-wider">Sustainability</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Eco-Friendly Technology</h2>
              <p className="text-gray-600 leading-relaxed mb-4">Goodman heat pumps use <strong>R-32 refrigerant</strong> - 67% lower Global Warming Potential than older R-410A. Higher heat transfer efficiency means less energy consumed.</p>
              <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                <div className="text-3xl font-black text-green-700">3,000 lbs</div>
                <p className="text-green-800 text-sm mt-1">CO2 emissions prevented annually vs a 10 SEER unit - equivalent to taking <strong>5 cars off the road</strong> over the system's lifespan.</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-10 flex items-center justify-center">
              <div className="text-center">
                <Leaf className="w-20 h-20 text-green-400 mx-auto mb-4 opacity-50" />
                <div className="text-5xl font-black text-green-700">67%</div>
                <div className="text-green-600 font-semibold mt-2">Lower Global Warming<br />Potential (R-32)</div>
              </div>
            </div>
          </section>

          {/* Whisper Quiet */}
          <section className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-3xl p-10 flex items-center justify-center">
              <div className="text-center">
                <Volume2 className="w-20 h-20 text-indigo-400 mx-auto mb-4 opacity-50" />
                <div className="text-5xl font-black text-indigo-700">45 <span className="text-2xl">dBA</span></div>
                <div className="text-indigo-600 font-semibold mt-2">Quiet Mode<br />Quieter than a library</div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center">
                  <Volume2 className="w-6 h-6 text-indigo-600" />
                </div>
                <span className="text-indigo-600 font-semibold text-sm uppercase tracking-wider">Comfort</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Whisper-Quiet Operation</h2>
              <p className="text-gray-600 leading-relaxed">Goodman's newest inverter-driven models run as low as <strong>56 dBA</strong> - about as quiet as a refrigerator. With Quiet Mode activated, sound levels drop to just <strong>45 dBA</strong> - quieter than a library.</p>
            </div>
          </section>

          {/* Made in America */}
          <section className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center">
                  <Flag className="w-6 h-6 text-red-600" />
                </div>
                <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">American Made</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Made in America, Priced for Everyone</h2>
              <p className="text-gray-600 leading-relaxed mb-4">Designed, engineered, and assembled right here in the U.S.A. US News & World Report ranks Goodman in the <strong>top 3 AC brands</strong>. ENERGY STAR Partner status with multiple certified models.</p>
              <div className="flex gap-3">
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-4 py-2">
                  <Star className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-semibold text-gray-700">Top 3 Brand</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-4 py-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm font-semibold text-gray-700">ENERGY STAR</span>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl p-10 flex items-center justify-center">
              <div className="text-center">
                <Flag className="w-20 h-20 text-red-400 mx-auto mb-4 opacity-50" />
                <div className="text-4xl font-black text-red-700">USA</div>
                <div className="text-red-600 font-semibold mt-2">Designed, Engineered<br />& Assembled</div>
              </div>
            </div>
          </section>
        </div>

        {/* Tax Credits */}
        <section className="bg-amber-50 border border-amber-200 rounded-3xl p-10 mb-20">
          <div className="flex items-start gap-5">
            <div className="w-14 h-14 bg-amber-400 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
              <DollarSign className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Tax Credits & Rebates Available</h2>
              <p className="text-gray-600 leading-relaxed mb-3">Depending on where you live in Florida, upgrading to a Goodman 15.2 SEER2 Heat Pump System can qualify you for rebates from <strong className="text-amber-700">$45 to $1,150</strong>.</p>
              <p className="text-gray-600 leading-relaxed">Combine that with your lower energy costs and your new Air Conditioning System can actually <strong>pay for itself</strong> in a relatively short period of time!</p>
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="text-center mb-20">
          <p className="text-xl text-gray-700 font-medium max-w-3xl mx-auto mb-8">A Goodman Heat Pump A/C System is an investment in comfort, savings, and sustainability - and given the hot climate here in Central Florida, it's one of the most practical and cost-effective choices you can make for your home.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/installation" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 py-4 rounded-xl transition-colors text-base shadow-lg">
              View Installation Pricing <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/unit-only" className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-bold px-8 py-4 rounded-xl transition-colors text-base shadow-lg">
              Shop Unit Only
            </Link>
          </div>
        </section>

        {/* Licensed Installation Section */}
        <section className="border-t border-gray-200 pt-16">
          <div className="text-center mb-12">
            <span className="inline-block bg-emerald-100 text-emerald-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">Professional Installation</span>
            <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4">Why Licensed and Insured Installation?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Using a licensed and insured contractor to install your Goodman heat pump protects both your investment and your home.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: 'Warranty Protection', desc: "Goodman's manufacturer warranty requires professional installation. Unlicensed installation can void your warranty, leaving you to pay for repairs out of pocket." },
              { title: 'Code Compliance & Permits', desc: 'Licensed contractors pull required permits and ensure work passes inspection. Unpermitted HVAC work creates problems when selling your home.' },
              { title: 'EPA Certification', desc: 'Anyone handling refrigerants must hold EPA Section 608 certification. Improperly charged systems run inefficiently and fail early.' },
              { title: 'Insurance Coverage', desc: "If an uninsured installer damages your home - floods, fires, injuries - you're exposed. Licensed contractors carry liability and workers' comp." },
              { title: 'Proper Sizing & Quality', desc: 'Poor installation is the #1 cause of early HVAC failure. Studies show roughly half of residential systems are installed incorrectly.' },
              { title: 'Recourse & Rebates', desc: 'Licensed contractors are accountable to state licensing boards. Most utility rebates and tax credits require licensed installation.' },
            ].map((item, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg hover:border-emerald-200 transition-all duration-300">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-emerald-600 font-bold text-sm">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 bg-gray-50 rounded-2xl p-8 border border-gray-200">
            <p className="text-gray-700 font-medium text-center max-w-3xl mx-auto"><strong>The bottom line:</strong> A licensed, insured contractor is what stands between you and the worst-case scenarios - a voided warranty, a failed inspection, an uninsured injury claim, or a system that quietly costs you money for the next 15 years because it was never commissioned correctly.</p>
          </div>
        </section>
      </main>

      <Footer />
      <AccessibilityWidget />
    </div>
  );
}
